import PAYMENT_METHOD from 'domains/customer-checkout/molecules/payment-method'
import { MINIMUM_CREDIT_CARD_INSTALLMENT_BCA_PRICE, MINIMUM_CREDIT_CARD_INSTALLMENT_BNI_PRICE, MINIMUM_KREDIVO_PRICE } from '../constants/checkout-constants'
import PriceUtil from 'utils/price-util'
import CustomerOrdersApiV2 from 'api/v2/admins/customer-orders-api-v2'
import { CATEGORY as PAYMENT_METHOD_CATEGORIES } from 'domains/payment-method/constants'

/**
 * Static class that holds business logic specific to the /chekcouts page
 */
class CustomerCheckoutsService {
  static generateShipmentModel(shipment, shipment_method) {
    // Prepare shipment items for backend
    const shipmentItemsModel = shipment.items.map((item) => {
      const itemModel = {
        cart_item_id : item.cart_item_id,
        inventory_id : item.inventory.id,
        quantity     : item.quantity,
        weight       : item.weight
      }
      return itemModel
    })

    // Rebuild to for backend (Customer::Shipment model)
    const shipmentModel = {
      // shipmentData
      tl_shipment_box_id    : shipment.tl_shipment_box_id,
      weight_type           : shipment.weight_type,
      weight                : shipment.weight,
      // selected_shipment_method
      tl_shipment_method_id : shipment_method.id,
      cost                  : shipment_method.cost,
      courier               : shipment_method.courier,
      courier_service_type  : shipment_method.service_type,
      // shipment insurance
      insurance_cost        : shipment_method.is_insurance ? shipment_method.insurance_cost : 0,
      // items
      items                 : shipmentItemsModel
    }

    return shipmentModel
  }

  static validate(selectedPaymentMethod, selectedShipmentMethod, checkoutOrder) {
    // Check if user has selected a payment method
    const userSelectedPaymentMethod = !!selectedPaymentMethod

    // Check if user selected shipment method for each shipment
    const userSelectedShipmentMethod = !!selectedShipmentMethod

    if (!userSelectedShipmentMethod) return 'Please selected a Shipment Method for your shipments.'
    if (!userSelectedPaymentMethod) return 'Please select a Payment Method.'

    // Check order total > 500,000 for installment_credit_card payment
    const installmentErrorMsg = this.validateInstallment(checkoutOrder.total, selectedPaymentMethod)
    if (installmentErrorMsg) return installmentErrorMsg

    return
  }

  static validateInstallment(itemTotal, paymentMethod) {
    if (paymentMethod.category === 'installment_without_cc') {
      if (itemTotal < MINIMUM_KREDIVO_PRICE) return `Minimum Item Subtotal to pay with ${paymentMethod.name} is ${PriceUtil .formatToIDR(MINIMUM_KREDIVO_PRICE)}`
    }

    // BNI installment case
    if ( paymentMethod.name.includes('BNI Installment Credit Card')) {
      if (itemTotal < MINIMUM_CREDIT_CARD_INSTALLMENT_BNI_PRICE) return `Minimum Item Subtotal to pay with ${paymentMethod.name} is ${PriceUtil.formatToIDR(MINIMUM_CREDIT_CARD_INSTALLMENT_BNI_PRICE)}`
    }

    // BCA installment case
    if ( paymentMethod.name.includes('BCA Installment Credit Card')) {
      if (itemTotal < MINIMUM_CREDIT_CARD_INSTALLMENT_BCA_PRICE) return `Minimum Item Subtotal to pay with ${paymentMethod.name} is ${PriceUtil.formatToIDR(MINIMUM_CREDIT_CARD_INSTALLMENT_BCA_PRICE)}`
    }
  }

  static validateApplyVoucher(address, paymentMethod, shipment) {
    if (!address)       return ('You must selected an Address first')
    if (!paymentMethod) return ('You must selected a Payment Method first.')
    if (!shipment)     return ('You must selected a Shipment first.')
  }
  // func to get url path to payment page based on payment method
  static getPaymentPath(paymentMethod) {
    switch (paymentMethod.category) {

      case PAYMENT_METHOD.CATEGORIES.BANK_TRANSFER:
        return '/checkout/payment/bank-transfer'

      case PAYMENT_METHOD.CATEGORIES.VIRTUAL_ACCOUNT:
        return '/checkout/payment/virtual-account'

      case PAYMENT_METHOD.CATEGORIES.CREDIT_CARD:
        return '/checkout/payment/credit-card'

      case PAYMENT_METHOD.CATEGORIES.INSTALLMENT_CREDIT_CARD:
        return '/checkout/payment/credit-card'

      case PAYMENT_METHOD.CATEGORIES.INSTALLMENT_WIHTOUT_CC:
        if (paymentMethod.name.toLowerCase() === 'kredivo - 30 days') return '/checkout/payment/kredivo'
        break

      case PAYMENT_METHOD.CATEGORIES.INSTANT_PAYMENT:
        if (paymentMethod.name.toLowerCase() === 'gopay') return '/checkout/payment/gopay'
        if (paymentMethod.name.toLowerCase() === 'bca klikpay') return '/checkout/payment/bca-klikpay'
        break

      case PAYMENT_METHOD.CATEGORIES.RETAIL_CASH:
        return '/checkout/payment/retail-cash'

      case PAYMENT_METHOD.CATEGORIES.COD:
        return '/checkout/payment/thank-you'
    }
  }

  /**
   * Create a request to create payment data in the backend based on the payment method category
   * @param {Number} paymentId - Customer::Order::Payment
   * @param {Number} orderId - Customer::Order.id
   * @param {Object} paymentMethod - TlPaymentMethod
   */
  static async createPayment({ paymentId, orderId, paymentMethod }) {
    switch (paymentMethod.category) {
      case PAYMENT_METHOD_CATEGORIES.BANK_TRANSFER:
        await CustomerOrdersApiV2.postBankTransfer(
          paymentId,
          orderId
        )
        break

      case PAYMENT_METHOD_CATEGORIES.VIRTUAL_ACCOUNT:
        await CustomerOrdersApiV2.postVirtualAccount(
          paymentMethod.url,
          paymentId,
          orderId
        )
        break

      case PAYMENT_METHOD_CATEGORIES.INSTANT_PAYMENT:
        if (paymentMethod.name.toLowerCase() === 'qris') await CustomerOrdersApiV2.postMidtransQris(paymentId)
        break

      case PAYMENT_METHOD_CATEGORIES.OFFLINE:
        if (paymentMethod.name === 'QRIS') await CustomerOrdersApiV2.postOfflineQris(paymentId)

        if (paymentMethod.name === 'Credit/Debit Card') await CustomerOrdersApiV2.postOfflineCreditCard(paymentId)
        break

      case PAYMENT_METHOD_CATEGORIES.CREDIT_CARD:
        await CustomerOrdersApiV2.postMidtransCreditCard(paymentId)
        break

      case PAYMENT_METHOD_CATEGORIES.INSTALLMENT_CREDIT_CARD:
        await CustomerOrdersApiV2.postMidtransCreditCard(paymentId)
        break

      default:
        return
    }
  }
}

export default CustomerCheckoutsService